import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import useStore from '../../store'

const NAV = [
  { to: '/canvas',   label: 'Canvas',   unlock: null,           desc: 'Your life timeline' },
  { to: '/goals',    label: 'Goals',    unlock: null,           desc: 'What you\'re working on' },
  { to: '/review',   label: 'Review',   unlock: null,           desc: 'Weekly check-in' },
  { to: '/analysis', label: 'Analysis', unlock: 'basic_analysis', desc: 'Patterns & accuracy' },
]

const PAGE_CONTEXT = {
  '/canvas':   'Each block is one week. Coloured blocks are time allocated to your goals.',
  '/goals':    'Committed goals are on your timeline. Backlog goals are parked until you\'re ready.',
  '/review':   'Log actual hours. The system learns how well you estimate over time.',
  '/analysis': 'Patterns from your completed phases and weekly reviews.',
}

export default function Shell() {
  const location   = useLocation()
  const reviewDue  = useStore(s => s.reviewDue)
  const has        = useStore(s => s.has)
  const freshUnlock = useStore(s => s.freshUnlock)
  const clearFresh = useStore(s => s.clearFresh)
  const profile    = useStore(s => s.profile)

  const visible = NAV.filter(n => !n.unlock || has(n.unlock))
  const context = PAGE_CONTEXT[location.pathname] ?? ''

  return (
    <div className="flex flex-col h-full bg-m-bg overflow-hidden">

      {/* ── Top bar ── */}
      <header className="shrink-0 border-b border-m-border flex items-center justify-between px-5 h-12">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-m-accent font-medium">MORI</span>
          </div>
          <div className="hidden sm:block h-3 w-px bg-m-border" />
          <span className="font-mono text-[10px] text-m-subtle hidden sm:block">what remains is yours</span>
        </div>

        {/* Nav */}
        <nav className="flex items-center h-full">
          {visible.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `relative flex items-center h-full font-mono text-[11px] tracking-widest uppercase px-4 transition-colors duration-150 ${
                  isActive ? 'text-m-text' : 'text-m-subtle hover:text-m-text'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute bottom-0 left-0 right-0 h-px bg-m-accent"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  {/* Review due dot */}
                  {item.to === '/review' && reviewDue && (
                    <span className="absolute top-2.5 right-2 w-1.5 h-1.5 bg-m-warn rounded-full" />
                  )}
                  {/* Fresh unlock dot */}
                  {item.unlock && freshUnlock === item.unlock && (
                    <span className="absolute top-2.5 right-2 w-1.5 h-1.5 bg-m-accent rounded-full animate-pulse-slow" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Profile info */}
        {profile && (
          <div className="hidden md:block font-mono text-[10px] text-m-faint">
            cap: {profile.active_goal_cap} · {profile.weekly_available_hours}h/wk
          </div>
        )}
      </header>

      {/* ── Review due banner ── */}
      {reviewDue && (
        <div className="shrink-0 bg-m-warn/5 border-b border-m-warn/20 px-5 py-1.5 flex items-center justify-between">
          <span className="font-mono text-[11px] text-m-warn">
            Weekly review pending — log your hours to track progress
          </span>
          <NavLink to="/review"
            className="font-mono text-[11px] text-m-warn underline underline-offset-2 hover:no-underline">
            Complete now →
          </NavLink>
        </div>
      )}

      {/* ── Fresh unlock banner ── */}
      {freshUnlock && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="shrink-0 bg-m-accent/8 border-b border-m-accent/20 px-5 py-1.5 flex items-center justify-between overflow-hidden"
        >
          <span className="font-mono text-[11px] text-m-accent">
            Unlocked: <strong>{freshUnlock.replace(/_/g, ' ')}</strong>
          </span>
          <button onClick={clearFresh}
            className="font-mono text-[10px] text-m-accentDim hover:text-m-accent">
            dismiss
          </button>
        </motion.div>
      )}

      {/* ── Page context hint (one line, helps user understand where they are) ── */}
      {context && (
        <div className="shrink-0 border-b border-m-border bg-m-surface/40 px-5 py-1.5">
          <p className="font-mono text-[10px] text-m-faint">{context}</p>
        </div>
      )}

      {/* ── Main content ── */}
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
